import React, { useState, useEffect, useRef } from 'react';
import tripService from '../../services/trip/tripService';
import { Reservation, VehicleRegistrationRequiredAction } from 'guest-data-types-ts-lib';
import { TranslationUtils, ValidationUtils, AuthUtils, VehicleUtils } from '../../utils';
import VehicleModal, { VehicleModalOptions, StateVehicleType } from '../VehicleModal/VehicleModal';
import segmentService from '../../services/segment/segmentService';
import { useDispatch } from 'react-redux';
import { fetchTripError, accessTokenExpired } from '../../store/actions';
import { VehicleActionPanel, VehiclePanelList } from '..';
import _ from 'lodash';
import './VehiclePanel.scss';
import ddLogger from '../../logging/ddLogger';

interface VehiclePanelProps {
    vehicleRegistration: VehicleRegistrationRequiredAction;
    reservation: Reservation;
}

export interface LocalVehicle {
    id: number;
    stateCode: string;
    stateName: string;
    plate: string;
    otherState: string;
}

export const VehiclePanel: React.FC<VehiclePanelProps> = (props) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [myGroupHasNoVehicle, setMyGroupHasNoVehicle] = useState<boolean>(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [vehicles, setVehicles] = useState<LocalVehicle[]>([]);
    const [selectedVehicle, setSelectedVehicle] = useState<LocalVehicle>({
        id: 0,
        stateCode: '0',
        stateName: '0',
        plate: '',
        otherState: '',
    });
    const [vehicleModalOption, setVehicleModalOption] = useState<VehicleModalOptions>(VehicleModalOptions.NO_VEHICLES);
    const { vehicleRegistration, reservation } = props;
    const myGroupHasNoVehicleRef = useRef<any>();
    const dispatch = useDispatch();

    useEffect(() => {
        setMyGroupHasNoVehicle(vehicleRegistration.opted_out);
        setVehicles(VehicleUtils.mapToLocalVehicles(vehicleRegistration.vehicles_registered));
    }, []);

    useEffect(() => {
        setIsSubmitDisabled(!isVehicleModalValid());
    }, [selectedVehicle]);

    const isVehicleModalValid = () => {
        if (vehicleModalOption === VehicleModalOptions.NO_VEHICLES) {
            return true;
        }

        if (selectedVehicle.stateCode === StateVehicleType.OTHER && ValidationUtils.isEmptyOrWhiteSpace(selectedVehicle.otherState)) {
            return false;
        }

        return !(ValidationUtils.isEmptyOrWhiteSpace(selectedVehicle.plate) || selectedVehicle.stateCode === '0');
    };

    const handleMyGroupHasNoVehicleChange = (e: any) => {
        if (myGroupHasNoVehicle) {
            setVehicleModalOption(VehicleModalOptions.ADD);
        } else {
            setVehicleModalOption(VehicleModalOptions.NO_VEHICLES);
        }
        setShowModal(true);
    };

    const handleMyGroupHasNoVehicleLabelClick = () => {
        if (myGroupHasNoVehicleRef) {
            setSelectedVehicle({ id: 0, stateCode: '0', stateName: '', plate: '', otherState: '' });
            myGroupHasNoVehicleRef.current.click();
        }
    };

    const handleSubmit = async () => {
        try {
            if (AuthUtils.isAccessTokenSessionExpired()) {
                handleClose();
                dispatch(accessTokenExpired(true));
                return;
            }

            setIsSubmitDisabled(true);
            setIsLoadingSubmit(true);
            const isLastVehicleRemoved = vehicleModalOption === VehicleModalOptions.REMOVE && vehicles.length === 1;
            if (vehicleModalOption === VehicleModalOptions.NO_VEHICLES || isLastVehicleRemoved) {
                await noVehicleSubmit();
            } else {
                await vehicleSubmit();
            }
            setIsSubmitDisabled(false);
            setIsLoadingSubmit(false);
            setShowModal(false);
        } catch (e) {
            setIsSubmitDisabled(false);
            setIsLoadingSubmit(false);
            dispatch(fetchTripError(e));
        }
    };

    const vehicleSubmit = async () => {
        const newVehicles = VehicleUtils.updateLocalVehicleList(vehicles, selectedVehicle, vehicleModalOption);
        await tripService.updateVehicles(reservation.id, VehicleUtils.mapToTripVehicles(newVehicles));
        segmentService.trackEvent('Trip Portal Vehicle', {
            action: 'Vehicle Updated',
            previous_vehicles: vehicles.length,
            new_vehicles: newVehicles.length,
        });
        ddLogger.info(`Vehicle Updated. ReservationId: ${reservation.id}`, {
            previous_vehicles: vehicles.length,
            new_vehicles: newVehicles.length,
        });
        setVehicles(newVehicles);

        if (vehicleModalOption === VehicleModalOptions.ADD && myGroupHasNoVehicle) {
            setMyGroupHasNoVehicle(false);
        }
    };

    const noVehicleSubmit = async () => {
        await tripService.optOutOfVehicleRegistration(reservation.id);
        segmentService.trackEvent('Trip Portal Vehicle', {
            action: 'Vehicle Opt Out',
            previous_vehicles: vehicles.length,
        });
        ddLogger.info(`Vehicle Opt Out. ReservationId: ${reservation.id}`, { previous_vehicles: vehicles.length });
        setVehicles([]);
        setMyGroupHasNoVehicle(true);
    };

    const handleEditVehicleClick = (vehicle: LocalVehicle) => {
        setSelectedVehicle(vehicle);
        setVehicleModalOption(VehicleModalOptions.EDIT);
        setShowModal(true);
    };

    const handleEditVehicleKeyDown = (e: any, vehicle: LocalVehicle) => {
        if (ValidationUtils.isEnterOrSpaceKey(e.keyCode)) {
            setSelectedVehicle(vehicle);
            setVehicleModalOption(VehicleModalOptions.EDIT);
            setShowModal(true);
        }
    };

    const handleAddVehicleClick = () => {
        setSelectedVehicle({ id: vehicles.length + 1, stateCode: '0', stateName: '', plate: '', otherState: '' });
        setVehicleModalOption(VehicleModalOptions.ADD);
        setShowModal(true);
    };

    const handleGotoRemoveClick = () => {
        const restoredVehicle = vehicles.find((v) => v.id === selectedVehicle.id);
        if (restoredVehicle) {
            setSelectedVehicle(restoredVehicle);
        }
        setVehicleModalOption(VehicleModalOptions.REMOVE);
    };

    const handleClose = () => setShowModal(false);

    const formatVehicleProperty = (propertyName: string, vehicle: LocalVehicle): LocalVehicle => {
        switch (propertyName) {
            case 'plate':
                vehicle.plate = VehicleUtils.formatPlate(vehicle.plate);
                return vehicle;

            case 'stateCode':
                if (selectedVehicle.stateCode !== StateVehicleType.OTHER && vehicle.stateCode === StateVehicleType.OTHER) {
                    vehicle.otherState = '';
                }
                return vehicle;

            case 'otherState':
                vehicle.otherState = VehicleUtils.formatOtherState(vehicle.otherState);
                return vehicle;

            default:
                return vehicle;
        }
    };

    const handlePropertyChange = (e: any) => {
        if (!selectedVehicle) {
            return null;
        }

        const target = e.target as HTMLInputElement;
        const newVehicle = { ..._.cloneDeep(selectedVehicle), [target.name]: target.value };
        setSelectedVehicle(formatVehicleProperty(target.name, newVehicle));
    };

    return (
        <div>
            <div className="registration-title">
                <h5 className="font-bold max-vehicle-title">
                    {`${TranslationUtils.get('list_your_vehicles')} (${TranslationUtils.get('max')} ${
                        vehicleRegistration.vehicles_allowed
                    })`}
                </h5>
            </div>
            {vehicles.length > 0 && (
                <VehiclePanelList
                    vehicles={vehicles}
                    onEditVehicleClick={handleEditVehicleClick}
                    onEditVehicleKeyDown={handleEditVehicleKeyDown}
                />
            )}
            <VehicleActionPanel
                vehicles={vehicles}
                vehiclesAllowed={vehicleRegistration.vehicles_allowed}
                onAddVehicleClick={handleAddVehicleClick}
                onMyGroupHasNoVehicleChange={handleMyGroupHasNoVehicleChange}
                onMyGroupHasNoVehicleLabelClick={handleMyGroupHasNoVehicleLabelClick}
                myGroupHasNoVehicle={myGroupHasNoVehicle}
                myGroupHasNoVehicleRef={myGroupHasNoVehicleRef}
            />
            <VehicleModal
                localVehicle={selectedVehicle}
                option={vehicleModalOption}
                show={showModal}
                isSubmitDisabled={isSubmitDisabled}
                isLoadingSubmit={isLoadingSubmit}
                onClose={handleClose}
                onGotoRemove={handleGotoRemoveClick}
                onPropertyChange={handlePropertyChange}
                onSubmit={handleSubmit}
            />
        </div>
    );
};
